import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { Credentials } from '@app/auth/models/credentials.model';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import { OIDCService } from '@app/auth/services/oidc.service';
import { AuthUser } from '@app/core/models/auth-user';
import { RouterService } from '@app/routing/router.service';
import { portalError, PortalError } from '@app/utils/error/portal-error';
import { isBlank } from '@app/utils/string';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Observable } from 'rxjs';
import { finalize } from 'rxjs/operators';

interface LoginForm {
  username: FormControl<string>;
  password: FormControl<string>;
}

@UntilDestroy()
@Component({
  selector: 'portal-login-form',
  templateUrl: './login-form.component.html',
  styleUrls: ['./login-form.component.scss'],
  standalone: false,
})
export class LoginFormComponent implements OnInit {
  @ViewChild('username') usernameElement: ElementRef;
  @ViewChild('passwordInput') passwordElement: ElementRef;

  error: PortalError | null = null;

  form = this.fb.group<LoginForm>({
    username: this.fb.control<string>(null, Validators.required),
    password: this.fb.control<string>(null),
  });

  loading: boolean = false;
  usernameIsChecked = false;

  constructor(
    private authService: AuthenticationService,
    private routerService: RouterService,
    private fb: FormBuilder,
    private oidcService: OIDCService
  ) {}

  ngOnInit(): void {
    this.form.controls.username.valueChanges.pipe(untilDestroyed(this)).subscribe((username) => {
      if (isBlank(username)) {
        this.form.controls.password.clearValidators();
        this.usernameIsChecked = false;
      }
    });
  }

  checkAuthMethodForUsername(): void {
    this.loading = true;
    const username = this.form.controls.username.value;
    this.authService
      .oidcSettings(username)

      .subscribe({
        error: this.handleAuthenticationError,
        next: (oidcSettings) => {
          if (oidcSettings.oidcEnabled === true) {
            // !fix for loading state!: when we redirect to keycloak and then go "back" in browser (in production)
            // the application is not reinitialized but restored (do not know yet why and how) and the `this.loading` will then still be `true`.
            // hence, we are explicitly setting it to `false` prior to redirect so that the login button does not hang in "loading" state.
            this.loading = false;
            this.oidcService
              .login(username, oidcSettings)
              .pipe(finalize(() => (this.loading = false)))
              .subscribe();
          } else {
            // direct login
            this.form.controls.username.disable();
            this.form.controls.password.setValidators(Validators.required);
            this.form.controls.password.updateValueAndValidity()
            this.usernameIsChecked = true;
            setTimeout(() => {
              this.passwordElement.nativeElement.focus();
            }, 0);
            this.loading = false;
          }
        },
      });
  }

  changeUsername() {
    this.form.controls.username.enable();
    this.form.controls.password.setValue(null);
    this.form.controls.password.clearValidators();
    this.form.controls.password.markAsPristine()
    this.form.controls.password.updateValueAndValidity();
    this.error = null;
    setTimeout(() => {
      this.usernameElement.nativeElement.focus();
    }, 0);

    this.usernameIsChecked = false;
  }

  submit() {
    if (this.form.valid) {
      this.handleAuthentication(this.authService.loginUser(this.form.getRawValue() as Credentials));
    }
  }

  handleAuthentication(authentication: Observable<AuthUser>) {
    this.loading = true;

    authentication
      .pipe(
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe({
        error: this.handleAuthenticationError,
        next: () => {
          const url = window.history.state?.triedUrl;

          if (url == null || url === '/login') {
            this.routerService.navigateToInitialRoute();
          } else {
            this.routerService.navigateByUrl(url);
          }
        },
      });
  }

  handleAuthenticationError = (error: HttpErrorResponse) => {
    this.loading = false;
    let message: string;

    if (error.status === 401 || error.status === 403) {
      message = 'login.err.credentials';
    } else if (error.status === 502) {
      message = 'general.err.maintenance';
    } else {
      message = 'generic.error';
    }

    this.error = portalError({
      cause: null,
      id: 'login',
      i18n: message,
    });
  };
}
